import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Auth, user } from '@angular/fire/auth';
import { Observable, from, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { MfaService } from '../services/mfa.service';

/**
 * Blocks privileged users (system admins / group admins) from the app once their
 * 2FA grace period has expired, redirecting them to the mandatory setup page.
 * The setup route itself is always allowed so users can enrol out of the block.
 */
@Injectable({ providedIn: 'root' })
export class MfaEnforcementGuard implements CanActivateChild {
  constructor(
    private auth: Auth,
    private router: Router,
    private mfaService: MfaService,
  ) {}

  canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Never block the setup route, or the user could not escape the block.
    if (state.url.startsWith('/security')) {
      return of(true);
    }

    // Wait for Firebase to restore the auth state before reading status, so a
    // must-enrol user cannot slip through on a cold page load.
    return user(this.auth).pipe(
      take(1),
      switchMap((currentUser) => {
        if (!currentUser) {
          // Not signed in — let the auth guard handle the redirect.
          return of(true);
        }

        return from(this.mfaService.refreshStatus()).pipe(
          switchMap((status) => of(
            status?.mustEnrol
              ? this.router.createUrlTree(['/security/setup'])
              : true,
          )),
        );
      }),
    );
  }
}
